import { ArrowRight } from "lucide-react";

import { useT } from "@/3-features/i18n/useT";
import { PATHS } from "@/3-features/navigation/routes";
import { useOnScrollReveal } from "@/4-components/animations/anime";
import { BlogCard, sortByRecency } from "@/4-components/composites/BlogCard";
import { DecMark } from "@/4-components/primitives/DecMark";
import { SectionHead } from "@/4-components/primitives/SectionHead";
import { Link } from "@/5-shared/routing";
import { useBlogPosts } from "@/6-api/queries";

/** The blog's name, as the eyebrow: the DEC lettermark stands in for the word "DEC". */
const BLOG_NAME = (
  <span className="inline-flex items-center gap-[0.4em]">
    <DecMark className="h-[0.8em] w-auto overflow-visible" />
    <span className="sr-only">DEC</span>
    Insights
  </span>
);

/** Latest-articles band, three most recent posts from the live blog API, then a link to all of them. */
export function BlogSection() {
  const { t } = useT();
  const { data } = useBlogPosts();
  const posts = sortByRecency(data ?? []).slice(0, 3);
  // Posts arrive async, so the first render returns null (no band, no ref). Re-arm
  // the reveal once they land, else the section mounts with its `[data-reveal]`
  // nodes stuck at opacity:0 and the whole band renders invisible.
  const ref = useOnScrollReveal<HTMLElement>({}, [posts.length]);

  // No published posts for this locale: drop the band rather than show an empty one.
  if (posts.length === 0) return null;

  return (
    <section ref={ref} className="section bg-zinc-50">
      <div className="shell">
        <SectionHead reveal eyebrow={BLOG_NAME} heading={t("blog.heading")} sub={t("blog.sub")} className="section-head--centered mb-12" />

        <div className="-mx-6 flex snap-x snap-mandatory gap-4 overflow-x-auto px-6 pb-4 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden md:mx-0 md:grid md:snap-none md:grid-cols-3 md:gap-6 md:overflow-visible md:px-0 md:pb-0">
          {posts.map((post) => (
            <div data-reveal key={post.id} className="w-[82%] shrink-0 snap-center md:w-auto">
              <BlogCard post={post} />
            </div>
          ))}
        </div>

        <div data-reveal className="mt-10 flex justify-center">
          <Link to={PATHS.blog} className="group btn btn-ghost">
            {t("blog.allLink")}
            <ArrowRight className="h-5 w-5 transition-transform group-hover:translate-x-1" />
          </Link>
        </div>
      </div>
    </section>
  );
}
